<template>
  <div class="structure-viewer-container">
    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>Loading structure...</p>
    </div>
    
    <!-- 错误状态 -->
    <div v-else-if="error" class="error-container">
      <div class="error-icon">
        <i class="fas fa-exclamation-triangle"></i>
      </div>
      <p class="error-message">{{ error }}</p>
      <div class="fallback-options">
        <a 
          :href="`https://www.rcsb.org/structure/${pdbId}`" 
          target="_blank"
          class="fallback-link"
        >
          <i class="fas fa-external-link-alt"></i> View on RCSB PDB
        </a>
      </div>
    </div>
    
    <!-- 查看器容器 -->
    <div v-show="!loading && !error" ref="viewerContainer" class="molstar-viewer"></div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from 'vue'

const props = defineProps<{
  pdbId: string
}>()

const viewerContainer = ref<HTMLElement | null>(null)
let viewer: any = null
const loading = ref(true)
const error = ref('')

const initViewer = async () => {
  if (!viewerContainer.value) {
    console.error('Viewer container not found')
    error.value = 'Failed to initialize viewer container'
    loading.value = false
    return
  }
  
  loading.value = true
  error.value = ''

  try {
    console.log('Initializing viewer for PDB ID:', props.pdbId)
    
    // 清理之前的实例
    if (viewer) {
      try {
        viewer.destroy()
      } catch (e) {
        console.error('Error destroying previous viewer:', e)
      }
      viewer = null
    }
    
    // 清理容器
    viewerContainer.value.innerHTML = ''

    // 创建新的 Mol* Viewer 实例
    viewer = new (window as any).PDBeMolstarPlugin()

    // 配置查看器
    const options: PDBeMolstarPlugin.Options = {
      customData: {
        url: `https://files.rcsb.org/download/${props.pdbId}.pdb`,
        format: 'pdb'
      },
      alphafoldView: false,
      bgColor: { r: 255, g: 255, b: 255 },
      hideCanvasControls: ['selection', 'animation'],
      hideStructureTitle: true,
      loadMaps: false,
      lighting: 'flat',
      validationAnnotation: false,
      defaultPreset: 'default',
      layout: {
        controls: false,
        landscape: true
      }
    }

    // 渲染查看器
    await viewer.render(viewerContainer.value, options)
    
    loading.value = false
    console.log('Structure loaded successfully')
  } catch (e: any) {
    console.error('Error in initViewer:', e)
    error.value = e.message || 'Failed to initialize viewer'
    loading.value = false
    
    // 清理失败的实例
    if (viewer) {
      try {
        viewer.destroy()
      } catch (disposeError) {
        console.error('Error disposing viewer:', disposeError)
      }
      viewer = null
    }
  }
}

// 监听 PDB ID 变化
watch(() => props.pdbId, async (newId) => {
  console.log('PDB ID changed to:', newId)
  if (newId) {
    await initViewer()
  }
}, { immediate: false })

onMounted(async () => {
  console.log('PDBViewer mounted with PDB ID:', props.pdbId)
  if (props.pdbId) {
    await initViewer()
  }
})

onUnmounted(() => {
  if (viewer) {
    try {
      viewer.destroy()
    } catch (e) {
      console.error('Error destroying viewer:', e)
    }
    viewer = null
  }
})
</script>

<style scoped>
.structure-viewer-container {
  width: 100%;
  height: 500px;
  position: relative;
  background-color: #f8f9fa;
  border-radius: 8px;
  overflow: hidden;
}

.molstar-viewer {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: block;
}

/* 确保 Mol* 插件的样式正确应用 */
:deep(.msp-plugin) {
  width: 100%;
  height: 100%;
}

:deep(.msp-viewport) {
  width: 100%;
  height: 100%;
}

.loading-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.9);
  z-index: 1;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

.error-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background-color: rgba(255, 255, 255, 0.9);
}

.error-icon {
  color: #e74c3c;
  font-size: 2rem;
  margin-bottom: 1rem;
}

.error-message {
  color: #2c3e50;
  text-align: center;
  margin-bottom: 1rem;
}

.fallback-options {
  display: flex;
  gap: 1rem;
}

.fallback-link {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: #f8f9fa;
  border-radius: 4px;
  color: #2c3e50;
  text-decoration: none;
  transition: background-color 0.2s;
}

.fallback-link:hover {
  background-color: #e9ecef;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>